import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api/client'
import type { UnreadPage, UnreadPlace } from '../api/types'
import { sendDirectRead } from '../direct/useConversation'

/** How long after the organisation stream's count rises the list asks again, so a burst of messages is one request. */
const FETCH_AGAIN_AFTER_MILLISECONDS = 1000

const NOTHING: ReadonlySet<string> = new Set()

export interface Unreads {
  /** Your agents' direct messages first, then rooms; null until the first page has landed. */
  places: UnreadPlace[] | null
  failed: boolean
  /** What the list still shows as unread, a dot each. A message read here leaves it and stays on screen. */
  unread: ReadonlySet<string>
  /** How many are new in a place: what it shows unread, and what it leaves out. */
  newIn: (place: UnreadPlace) => number
  /** Seen in the list: sent to each room, and to each agent as the newest private message seen. */
  markSeen: (ids: string[]) => void
  /** Everything in the place read, up to its last message. */
  markPlaceRead: (place: UnreadPlace) => void
  markAllRead: () => void
}

/**
 * The room open behind the list. What is read of it here goes through the
 * room's own read state, which loads once per room, so that going back to
 * it shows no dot over what was just read.
 */
export interface OpenRoom {
  channelId: string
  markSeen: (ids: string[]) => void
  markRead: (through: string) => void
}

export function keyOf(place: UnreadPlace): string {
  return place.kind === 'room' ? `room ${place.channel_id}` : `direct ${place.agent}`
}

/** Oldest first, as the server sends them; ids sort by time as strings. */
export function idsOf(place: UnreadPlace): string[] {
  return (place.kind === 'room' ? place.messages : place.direct_messages).map((message) => message.id)
}

function union<Item extends { id: string }>(shown: Item[], fetched: Item[]): Item[] {
  const byId = new Map(shown.map((item) => [item.id, item]))
  for (const item of fetched) byId.set(item.id, item)
  return [...byId.values()].sort((first, second) => (first.id < second.id ? -1 : first.id > second.id ? 1 : 0))
}

/**
 * A page fetched again over what the list shows. Nothing shown leaves: a
 * place keeps its position and its messages, and what arrives joins it. A
 * place new to the list goes after the shown ones of its kind, so direct
 * messages stay first and nothing moves under the reader.
 */
function merged(shown: UnreadPlace[], page: UnreadPage, seen: ReadonlySet<string>): {
  places: UnreadPlace[]
  fresh: ReadonlySet<string>
} {
  // What the newest page holds is unread, and a message it left out was read somewhere else.
  const fresh = new Set(page.places.flatMap(idsOf))
  const fetched = new Map(page.places.map((place) => [keyOf(place), place]))
  const kept = shown.map((place): UnreadPlace => {
    const again = fetched.get(keyOf(place))
    // Gone from the page means nothing in it is unread any more.
    if (!again) return { ...place, earlier: 0 }
    // Except in a place with more than the page holds: there what arrived
    // pushed the oldest shown out of the page and into `earlier`, still
    // unread unless it was read here, which `earlier` already leaves out.
    const pushed = again.earlier > 0
      ? idsOf(place).filter((id) => id < idsOf(again)[0] && !seen.has(id))
      : []
    for (const id of pushed) fresh.add(id)
    // A read sent from here may not have landed when the page was counted.
    const earlier = Math.max(0, again.earlier - pushed.length)
    return again.kind === 'room'
      ? { ...again, earlier, messages: union(place.messages, again.messages) }
      : { ...again, earlier, direct_messages: union(place.direct_messages, again.direct_messages) }
  })
  const known = new Set(shown.map(keyOf))
  const all = [...kept, ...page.places.filter((place) => !known.has(keyOf(place)))]
  return {
    places: [...all.filter((place) => place.kind === 'direct'), ...all.filter((place) => place.kind === 'room')],
    fresh,
  }
}

/**
 * The Unreads list while it is open: the page fetched on opening, and again a
 * second after the organisation stream's total, `counts`, rises. What is read
 * here stays in the list until it closes, as in a room.
 */
export function useUnreads(organisation: string | null, counts: number, openRoom?: OpenRoom): Unreads {
  const [held, setHeld] = useState<{ places: UnreadPlace[]; fresh: ReadonlySet<string> } | null>(null)
  const [failed, setFailed] = useState(false)
  const [seen, setSeen] = useState<ReadonlySet<string>>(NOTHING)
  const [asked, setAsked] = useState(0)
  const counted = useRef(counts)
  const timer = useRef<number | undefined>(undefined)
  // Read by `markSeen`, which stays one function so a second's reads go out as one batch.
  const places = useRef<UnreadPlace[]>([])
  places.current = held?.places ?? []
  const room = useRef(openRoom)
  room.current = openRoom
  // Read by the merge when a page lands, after the render that last changed it.
  const seenNow = useRef(seen)
  seenNow.current = seen

  // Not reset by a falling count: reading lowers it, and a rise just before should still be fetched.
  useEffect(() => {
    if (counts > counted.current) {
      window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => setAsked((times) => times + 1), FETCH_AGAIN_AFTER_MILLISECONDS)
    }
    counted.current = counts
  }, [counts])
  useEffect(() => () => window.clearTimeout(timer.current), [])

  useEffect(() => {
    if (!organisation) return
    let current = true
    api.unread(organisation).then(
      (page) => { if (current) setHeld((previous) => merged(previous?.places ?? [], page, seenNow.current)) },
      () => { if (current) setFailed(true) },
    )
    return () => { current = false }
  }, [organisation, asked])

  const unread = useMemo(
    () => new Set([...held?.fresh ?? []].filter((id) => !seen.has(id))),
    [held?.fresh, seen],
  )

  const newIn = useCallback(
    (place: UnreadPlace) => idsOf(place).filter((id) => unread.has(id)).length + place.earlier,
    [unread],
  )

  const markSeen = useCallback((ids: string[]) => {
    setSeen((previous) => new Set([...previous, ...ids]))
    for (const place of places.current) {
      const here = idsOf(place).filter((id) => ids.includes(id))
      if (!here.length) continue
      if (place.kind === 'direct') {
        if (organisation) sendDirectRead(organisation, place.agent, here[here.length - 1])
      } else if (room.current?.channelId === place.channel_id) room.current.markSeen(here)
      else void api.markSeen(place.channel_id, here)
    }
  }, [organisation])

  const markPlaceRead = useCallback((place: UnreadPlace) => {
    const ids = idsOf(place)
    const last = ids[ids.length - 1]
    if (!last || !organisation) return
    setSeen((previous) => new Set([...previous, ...ids]))
    setHeld((previous) => previous && {
      ...previous,
      places: previous.places.map((shown) => (keyOf(shown) === keyOf(place) ? { ...shown, earlier: 0 } : shown)),
    })
    if (place.kind === 'direct') sendDirectRead(organisation, place.agent, last)
    else if (room.current?.channelId === place.channel_id) room.current.markRead(last)
    else void api.markRead(place.channel_id, last)
  }, [organisation])

  const markAllRead = useCallback(() => {
    for (const place of places.current) if (newIn(place) > 0) markPlaceRead(place)
  }, [newIn, markPlaceRead])

  return {
    places: held?.places ?? null, failed, unread, newIn, markSeen, markPlaceRead, markAllRead,
  }
}
